import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  Siren,
  Sparkles,
  X,
} from 'lucide-react';
import {
  AMBULANCE_FACILITIES,
  AMBULANCE_TYPES,
  compareAmbulanceServiceAreas,
  CompareAreaGroup,
  CompareVendorRow,
  CoverageFilter,
  AmbulanceServiceAreaCompareSummary,
  getAmbulancePlaceDetails,
  PlaceDetails,
  searchAmbulancePlaces,
  suggestAmbulancesWithPricing,
  SuggestAmbulanceData,
  SuggestAmbulanceRow,
} from '../../services/MasterAdmin/ambulanceServiceAreaCompareService';

type PageTab = 'explore' | 'suggest' | 'exclusive';
type ActiveFilter = '' | 'true' | 'false';

type FilterState = {
  pinCode: string;
  areaName: string;
  search: string;
  coverage: CoverageFilter;
  isActive: ActiveFilter;
  includeInactiveVendors: boolean;
};

const defaultFilters: FilterState = {
  pinCode: '',
  areaName: '',
  search: '',
  coverage: 'all',
  isActive: 'true',
  includeInactiveVendors: false,
};

type PlaceValue = {
  address: string;
  lat: number | null;
  lng: number | null;
  pinCode: string;
};

const emptyPlace: PlaceValue = { address: '', lat: null, lng: null, pinCode: '' };

const inputClass =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:focus:border-teal-400';

const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400';

const AmbulanceServiceAreaComparePage: React.FC = () => {
  const [tab, setTab] = useState<PageTab>('explore');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-slate-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">
        <header className="mb-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">
                Operations
              </p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white md:text-3xl">
                Ambulance service areas
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                Compare coverage, find non-overlapping providers for targeted requests, and look up
                eligible ambulances with suggested agency pricing.
              </p>
            </div>
          </div>

          <nav className="mt-6 flex flex-wrap gap-2 rounded-2xl border border-slate-200/80 bg-white/80 p-1.5 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-800/80">
            <TabButton active={tab === 'explore'} onClick={() => setTab('explore')} icon={<Search className="h-4 w-4" />}>
              Explore coverage
            </TabButton>
            <TabButton active={tab === 'suggest'} onClick={() => setTab('suggest')} icon={<Siren className="h-4 w-4" />}>
              Get ambulance
            </TabButton>
            <TabButton
              active={tab === 'exclusive'}
              onClick={() => setTab('exclusive')}
              icon={<ShieldCheck className="h-4 w-4" />}
            >
              Non-conflict areas
            </TabButton>
          </nav>
        </header>

        {tab === 'explore' ? <ExploreCoveragePanel /> : null}
        {tab === 'suggest' ? <SuggestAmbulancePanel /> : null}
        {tab === 'exclusive' ? <ExclusiveCoveragePanel /> : null}
      </div>
    </div>
  );
};

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition min-[480px]:flex-none ${
        active
          ? 'bg-teal-600 text-white shadow-sm'
          : 'text-slate-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700/60'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

/* ─── Explore coverage ─────────────────────────────────────────────────── */

function ExploreCoveragePanel() {
  const [draft, setDraft] = useState<FilterState>(defaultFilters);
  const [applied, setApplied] = useState<FilterState>(defaultFilters);
  const [summary, setSummary] = useState<AmbulanceServiceAreaCompareSummary | null>(null);
  const [areas, setAreas] = useState<CompareAreaGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async (filters: FilterState) => {
    try {
      setLoading(true);
      setError(null);
      const res = await compareAmbulanceServiceAreas({
        pinCode: filters.pinCode,
        areaName: filters.areaName,
        search: filters.search,
        coverage: filters.coverage,
        isActive: filters.isActive === '' ? '' : filters.isActive === 'true',
        includeInactiveVendors: filters.includeInactiveVendors,
      });
      if (res.success && res.data) {
        setSummary(res.data.summary);
        setAreas(res.data.areas);
        const next: Record<string, boolean> = {};
        res.data.areas.slice(0, 6).forEach((a) => {
          next[a.areaKey] = true;
        });
        setExpanded(next);
      } else {
        setError(res.message || 'Failed to load service areas');
        setSummary(null);
        setAreas([]);
      }
    } catch (err: unknown) {
      setError(extractError(err) || 'Failed to load data.');
      setSummary(null);
      setAreas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(applied);
  }, [applied, load]);

  return (
    <div className="space-y-5">
      <FilterCard
        title="Search & filters"
        subtitle="Find agencies by pin, area name, or free-text search. Toggle coverage to compare overlaps."
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setApplied({ ...draft });
          }}
          className="space-y-4"
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={draft.search}
              onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
              placeholder="Search agency, phone, email, vendor id, pin…"
              className={`${inputClass} pl-10`}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className={labelClass}>Pin code</span>
              <input
                type="search"
                inputMode="numeric"
                placeholder="400001"
                value={draft.pinCode}
                onChange={(e) => setDraft((d) => ({ ...d, pinCode: e.target.value }))}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className={labelClass}>Area name</span>
              <input
                type="search"
                placeholder="Andheri, Hinjewadi…"
                value={draft.areaName}
                onChange={(e) => setDraft((d) => ({ ...d, areaName: e.target.value }))}
                className={inputClass}
              />
            </label>
            <label className="block">
              <span className={labelClass}>Area status</span>
              <select
                value={draft.isActive}
                onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.value as ActiveFilter }))}
                className={inputClass}
              >
                <option value="true">Active only</option>
                <option value="false">Inactive only</option>
                <option value="">All</option>
              </select>
            </label>
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={draft.includeInactiveVendors}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, includeInactiveVendors: e.target.checked }))
                }
                className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              Include inactive vendors
            </label>
          </div>

          <div>
            <span className={labelClass}>Coverage mode</span>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { id: 'all', label: 'All areas' },
                  { id: 'unique', label: 'One ambulance only' },
                  { id: 'overlap', label: 'Overlapping' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, coverage: opt.id }))}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
                    draft.coverage === opt.id
                      ? 'bg-slate-900 text-white dark:bg-teal-600'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-gray-700">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-teal-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Apply filters
            </button>
            <button
              type="button"
              onClick={() => void load(applied)}
              disabled={loading}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(defaultFilters);
                setApplied(defaultFilters);
              }}
              className="inline-flex h-11 items-center gap-2 rounded-xl px-3 text-sm font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          </div>
        </form>
      </FilterCard>

      {error ? <ErrorBanner message={error} /> : null}

      {summary && !loading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
          <StatCard label="Service areas" value={summary.totalServiceAreas} />
          <StatCard label="Matched groups" value={summary.matchedAreaGroups} />
          <StatCard label="Vendors" value={summary.totalVendors} />
          <StatCard label="Unique pins" value={summary.uniquePinCodes} />
          <StatCard label="Non-conflict" value={summary.uniqueCoverageAreas} tone="good" />
          <StatCard label="Overlaps" value={summary.overlapAreas} tone="warn" />
        </div>
      ) : loading ? (
        <LoadingBlock />
      ) : null}

      <AreaResultsList
        areas={areas}
        loading={loading}
        expanded={expanded}
        setExpanded={setExpanded}
        emptyHint="No matching service areas. Try another pin or area name."
      />
    </div>
  );
}

/* ─── Exclusive / non-conflict ─────────────────────────────────────────── */

function ExclusiveCoveragePanel() {
  const [pinCode, setPinCode] = useState('');
  const [areaName, setAreaName] = useState('');
  const [search, setSearch] = useState('');
  const [areas, setAreas] = useState<CompareAreaGroup[]>([]);
  const [exclusiveVendors, setExclusiveVendors] = useState<CompareVendorRow[]>([]);
  const [summary, setSummary] = useState<AmbulanceServiceAreaCompareSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await compareAmbulanceServiceAreas({
        pinCode,
        areaName,
        search,
        coverage: 'unique',
        isActive: true,
      });
      if (res.success && res.data) {
        setSummary(res.data.summary);
        setAreas(res.data.areas);
        setExclusiveVendors(res.data.exclusiveVendors || []);
        const next: Record<string, boolean> = {};
        res.data.areas.slice(0, 8).forEach((a) => {
          next[a.areaKey] = true;
        });
        setExpanded(next);
      } else {
        setError(res.message || 'Failed to load');
      }
    } catch (err: unknown) {
      setError(extractError(err) || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [pinCode, areaName, search]);

  useEffect(() => {
    void load();
  }, []); // initial only; search via button

  return (
    <div className="space-y-5">
      <FilterCard
        title="Non-conflict service areas"
        subtitle="Areas covered by exactly one ambulance agency — safe to send a targeted request without coverage conflict."
        accent
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void load();
          }}
          className="grid gap-3 md:grid-cols-4"
        >
          <label className="block md:col-span-1">
            <span className={labelClass}>Pin code</span>
            <input
              value={pinCode}
              onChange={(e) => setPinCode(e.target.value)}
              placeholder="Optional"
              className={inputClass}
            />
          </label>
          <label className="block md:col-span-1">
            <span className={labelClass}>Area name</span>
            <input
              value={areaName}
              onChange={(e) => setAreaName(e.target.value)}
              placeholder="Optional"
              className={inputClass}
            />
          </label>
          <label className="block md:col-span-1">
            <span className={labelClass}>Agency search</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Agency or phone"
              className={inputClass}
            />
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Find exclusive
            </button>
          </div>
        </form>
      </FilterCard>

      {error ? <ErrorBanner message={error} /> : null}

      {summary && !loading ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Exclusive areas" value={summary.matchedAreaGroups} tone="good" />
          <StatCard label="Exclusive providers" value={exclusiveVendors.length} tone="good" />
          <StatCard label="Total overlaps (network)" value={summary.overlapAreas} tone="warn" />
          <StatCard label="Active service areas" value={summary.totalServiceAreas} />
        </div>
      ) : loading ? (
        <LoadingBlock />
      ) : null}

      {exclusiveVendors.length > 0 && !loading ? (
        <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-200">
            <Sparkles className="h-4 w-4" />
            Providers safe for targeted requests
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {exclusiveVendors.map((v) => (
              <div
                key={v.vendorId}
                className="rounded-xl border border-emerald-100 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
              >
                <div className="font-medium text-slate-900 dark:text-white">
                  {v.agencyName || 'Unnamed agency'}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{v.contactNumber || v.phoneNumber || '—'}</div>
                <div className="mt-2 text-xs text-slate-600 dark:text-gray-400">
                  Exclusive pins:{' '}
                  {(v.exclusiveAreas || [])
                    .map((a) => a.pinCode || a.displayName)
                    .filter(Boolean)
                    .slice(0, 4)
                    .join(', ') || '—'}
                  {(v.exclusiveAreas || []).length > 4 ? '…' : ''}
                </div>
                <div className="mt-3 flex gap-3">
                  <Link
                    to={`/vendors/ambulance/${v.vendorId}`}
                    className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
                  >
                    Open vendor
                  </Link>
                  <Link
                    to={`/vendors/ambulance/${v.vendorId}/service-areas`}
                    className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
                  >
                    Areas
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <AreaResultsList
        areas={areas}
        loading={loading}
        expanded={expanded}
        setExpanded={setExpanded}
        emptyHint="No exclusive (non-overlap) areas found for these filters."
        highlightExclusive
      />
    </div>
  );
}

/* ─── Suggest / get ambulance ──────────────────────────────────────────── */

function SuggestAmbulancePanel() {
  const [ambulanceType, setAmbulanceType] = useState('EECO_AMBULANCE_BLS');
  const [facility, setFacility] = useState('WITH_OXYGEN');
  const [attendant, setAttendant] = useState(false);
  const [isDeadBody, setIsDeadBody] = useState(false);
  const [exclusiveOnly, setExclusiveOnly] = useState(false);
  const [pickup, setPickup] = useState<PlaceValue>(emptyPlace);
  const [drop, setDrop] = useState<PlaceValue>(emptyPlace);
  const [manualKm, setManualKm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuggestAmbulanceData | null>(null);

  const runSuggest = async () => {
    try {
      setLoading(true);
      setError(null);
      const facilities = [facility];
      if (attendant) facilities.push('ATTENDANT');

      const body = {
        ambulanceType,
        facilities: ambulanceType === 'CARDIAC_AMBULANCE_ALS' ? (attendant ? ['ATTENDANT'] : []) : facilities,
        isDeadBody,
        exclusiveOnly,
        pickupPinCode: pickup.pinCode || undefined,
        pickupLat: pickup.lat,
        pickupLng: pickup.lng,
        pickupAddress: pickup.address || undefined,
        dropLat: drop.lat,
        dropLng: drop.lng,
        dropAddress: drop.address || undefined,
        totalDistanceKm: manualKm.trim() ? Number(manualKm) : null,
      };

      const res = await suggestAmbulancesWithPricing(body);
      if (res.success && res.data) {
        setResult(res.data);
      } else {
        setResult(null);
        setError(res.message || 'No suggestions returned');
      }
    } catch (err: unknown) {
      setResult(null);
      setError(extractError(err) || 'Failed to suggest ambulances');
    } finally {
      setLoading(false);
    }
  };

  const isBls = ambulanceType !== 'CARDIAC_AMBULANCE_ALS';

  return (
    <div className="space-y-5">
      <FilterCard
        title="Get ambulance details"
        subtitle="Enter trip details to find eligible agencies with suggested pricing from their rate cards."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <label className="block">
            <span className={labelClass}>Ambulance type</span>
            <select
              value={ambulanceType}
              onChange={(e) => setAmbulanceType(e.target.value)}
              className={inputClass}
            >
              {AMBULANCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          {isBls ? (
            <label className="block">
              <span className={labelClass}>Facility</span>
              <select value={facility} onChange={(e) => setFacility(e.target.value)} className={inputClass}>
                {AMBULANCE_FACILITIES.filter((f) => f.value !== 'ATTENDANT').map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="flex items-end">
              <p className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5 text-sm text-slate-500 dark:border-gray-600">
                Cardiac (ALS) is priced by distance; facility oxygen options do not apply.
              </p>
            </div>
          )}

          <PlaceAutocomplete
            label="Pickup address"
            value={pickup}
            onChange={setPickup}
            preferPin
          />
          <PlaceAutocomplete label="Drop address" value={drop} onChange={setDrop} />

          <label className="block">
            <span className={labelClass}>Pickup pin (optional override)</span>
            <input
              value={pickup.pinCode}
              onChange={(e) => setPickup((p) => ({ ...p, pinCode: e.target.value }))}
              placeholder="From place or enter manually"
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Distance km (optional override)</span>
            <input
              type="number"
              min={0}
              step="0.1"
              value={manualKm}
              onChange={(e) => setManualKm(e.target.value)}
              placeholder="Auto from pickup → drop if blank"
              className={inputClass}
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={attendant}
              onChange={(e) => setAttendant(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-teal-600"
            />
            Attendant
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={isDeadBody}
              onChange={(e) => setIsDeadBody(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-teal-600"
            />
            Mortuary / dead body
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={exclusiveOnly}
              onChange={(e) => setExclusiveOnly(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-teal-600"
            />
            Only non-conflict (exclusive) providers
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4 dark:border-gray-700">
          <button
            type="button"
            onClick={() => void runSuggest()}
            disabled={loading}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-teal-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Siren className="h-4 w-4" />}
            Get ambulances
          </button>
          <button
            type="button"
            onClick={() => {
              setPickup(emptyPlace);
              setDrop(emptyPlace);
              setManualKm('');
              setResult(null);
              setError(null);
            }}
            className="inline-flex h-11 items-center rounded-xl px-3 text-sm text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
          >
            Reset
          </button>
        </div>
      </FilterCard>

      {error ? <ErrorBanner message={error} /> : null}

      {loading ? <LoadingBlock /> : null}

      {result && !loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Eligible" value={result.summary.eligibleCount} />
            <StatCard label="With pricing" value={result.summary.withPricing} tone="good" />
            <StatCard
              label="Lowest fare"
              value={result.summary.lowestFare != null ? Math.round(result.summary.lowestFare) : 0}
              prefix="₹"
              tone="good"
            />
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Trip</div>
              <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                {result.trip.distanceKm} km
              </div>
              <div className="text-xs text-slate-500">
                via {result.trip.distanceSource || '—'}
                {result.trip.durationText ? ` · ${result.trip.durationText}` : ''}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Suggested agencies
              </h3>
            </div>
            {result.suggestions.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-slate-500">
                No eligible ambulances for this pickup / type / facilities.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Agency</th>
                      <th className="px-4 py-3 font-semibold">Contact</th>
                      <th className="px-4 py-3 font-semibold">Location</th>
                      <th className="px-4 py-3 font-semibold">Suggested price</th>
                      <th className="px-4 py-3 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                    {result.suggestions.map((s) => (
                      <SuggestionRow key={s.vendorId} row={s} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SuggestionRow({ row }: { row: SuggestAmbulanceRow }) {
  return (
    <tr className="align-top hover:bg-slate-50/80 dark:hover:bg-gray-900/40">
      <td className="px-4 py-3">
        <div className="font-medium text-slate-900 dark:text-white">{row.agencyName || '—'}</div>
        <div className="font-mono text-[11px] text-slate-500">{row.generatedId || row.vendorId}</div>
        <div className="mt-1 flex flex-wrap gap-1">
          {row.exclusiveCoverage ? (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
              Exclusive
            </span>
          ) : null}
          {row.is24x7Available ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-700 dark:bg-gray-700 dark:text-gray-200">
              24×7
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3 text-slate-700 dark:text-gray-300">
        <div>{row.contactNumber || row.phoneNumber || '—'}</div>
        <div className="text-xs text-slate-500">{row.email || '—'}</div>
      </td>
      <td className="px-4 py-3 text-slate-700 dark:text-gray-300">
        {[row.city, row.state].filter(Boolean).join(', ') || '—'}
        {row.numOfAmbulances != null ? (
          <div className="text-xs text-slate-500">Fleet {row.numOfAmbulances}</div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        {row.pricing ? (
          <div>
            <div className="flex items-center gap-0.5 text-base font-semibold text-slate-900 dark:text-white">
              <IndianRupee className="h-4 w-4" />
              {row.pricing.totalPayableAmount.toLocaleString('en-IN')}
            </div>
            <div className="text-[11px] text-slate-500">{row.pricing.pricingSource}</div>
          </div>
        ) : (
          <span className="text-xs text-amber-700">No matching rate card</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <Link
            to={`/vendors/ambulance/${row.vendorId}`}
            className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
          >
            Open vendor
          </Link>
          <Link
            to={`/vendors/ambulance/${row.vendorId}/pricing`}
            className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400"
          >
            Pricing
          </Link>
        </div>
      </td>
    </tr>
  );
}

/* ─── Shared UI ────────────────────────────────────────────────────────── */

function PlaceAutocomplete({
  label,
  value,
  onChange,
  preferPin,
}: {
  label: string;
  value: PlaceValue;
  onChange: (v: PlaceValue) => void;
  preferPin?: boolean;
}) {
  const [query, setQuery] = useState(value.address);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Array<{ placeId: string; description: string }>>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQuery(value.address);
  }, [value.address]);

  const onType = (text: string) => {
    setQuery(text);
    onChange({ ...value, address: text });
    if (timer.current) clearTimeout(timer.current);
    if (text.trim().length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        setLoading(true);
        const list = await searchAmbulancePlaces(text.trim());
        setItems(list);
        setOpen(true);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const pick = async (placeId: string, description: string) => {
    try {
      setLoading(true);
      const details: PlaceDetails | null = await getAmbulancePlaceDetails(placeId);
      if (details) {
        onChange({
          address: details.formattedAddress || description,
          lat: details.latitude,
          lng: details.longitude,
          pinCode: preferPin ? details.postalCode || value.pinCode : value.pinCode || details.postalCode || '',
        });
        setQuery(details.formattedAddress || description);
      } else {
        onChange({ ...value, address: description });
        setQuery(description);
      }
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <label className="relative block">
      <span className={labelClass}>{label}</span>
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          placeholder="Start typing an address…"
          className={`${inputClass} pl-10 pr-9`}
          autoComplete="off"
        />
        {loading ? (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />
        ) : null}
      </div>
      {value.lat != null && value.lng != null ? (
        <p className="mt-1 text-[11px] text-slate-500">
          {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
          {value.pinCode ? ` · pin ${value.pinCode}` : ''}
        </p>
      ) : null}
      {open && items.length > 0 ? (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-900">
          {items.map((it) => (
            <li key={it.placeId}>
              <button
                type="button"
                onClick={() => void pick(it.placeId, it.description)}
                className="flex w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-teal-50 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                {it.description}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </label>
  );
}

function FilterCard({
  title,
  subtitle,
  children,
  accent,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <section
      className={`rounded-2xl border p-5 shadow-sm ${
        accent
          ? 'border-emerald-200/90 bg-gradient-to-br from-white to-emerald-50/40 dark:border-emerald-900/40 dark:from-gray-800 dark:to-emerald-950/20'
          : 'border-slate-200/90 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function StatCard({
  label,
  value,
  tone,
  prefix,
}: {
  label: string;
  value: number;
  tone?: 'good' | 'warn';
  prefix?: string;
}) {
  const accent =
    tone === 'good'
      ? 'border-l-emerald-500'
      : tone === 'warn'
        ? 'border-l-amber-500'
        : 'border-l-slate-800 dark:border-l-teal-500';
  return (
    <div
      className={`rounded-2xl border border-slate-200 border-l-4 bg-white px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${accent}`}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
        {prefix}
        {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
      </div>
    </div>
  );
}

function AreaResultsList({
  areas,
  loading,
  expanded,
  setExpanded,
  emptyHint,
  highlightExclusive,
}: {
  areas: CompareAreaGroup[];
  loading: boolean;
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  emptyHint: string;
  highlightExclusive?: boolean;
}) {
  const expandAll = () => {
    const next: Record<string, boolean> = {};
    areas.forEach((a) => {
      next[a.areaKey] = true;
    });
    setExpanded(next);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
          Coverage by area
          {!loading ? <span className="ml-2 font-normal text-slate-500">({areas.length})</span> : null}
        </h2>
        <div className="flex gap-2">
          <button type="button" onClick={expandAll} className="rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Expand all
          </button>
          <button type="button" onClick={() => setExpanded({})} className="rounded-lg px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 dark:text-gray-300 dark:hover:bg-gray-700">
            Collapse
          </button>
        </div>
      </div>

      {areas.length === 0 && !loading ? (
        <div className="px-4 py-12 text-center text-sm text-slate-500">{emptyHint}</div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-gray-700">
          {areas.map((area) => {
            const open = !!expanded[area.areaKey];
            return (
              <li key={area.areaKey}>
                <button
                  type="button"
                  onClick={() =>
                    setExpanded((prev) => ({ ...prev, [area.areaKey]: !prev[area.areaKey] }))
                  }
                  className="flex w-full items-start gap-3 px-4 py-3.5 text-left hover:bg-slate-50 dark:hover:bg-gray-900/40"
                >
                  <span className="mt-0.5 text-slate-400">
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </span>
                  <MapPin
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                      highlightExclusive || area.coverageType === 'unique'
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900 dark:text-white">
                        {area.pinCode ? `Pin ${area.pinCode}` : area.displayName || 'Unnamed area'}
                      </span>
                      {area.coverageType === 'unique' ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
                          <Check className="h-3 w-3" /> Non-conflict
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                          Overlap · {area.vendorCount}
                        </span>
                      )}
                    </div>
                    {area.areaNames.length > 0 ? (
                      <p className="mt-0.5 truncate text-xs text-slate-500">{area.areaNames.join(' · ')}</p>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-xs tabular-nums text-slate-500">
                    {area.vendorCount} agenc{area.vendorCount === 1 ? 'y' : 'ies'}
                  </span>
                </button>
                {open ? <AreaVendorTable area={area} /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AreaVendorTable({ area }: { area: CompareAreaGroup }) {
  return (
    <div className="overflow-x-auto bg-slate-50/80 px-4 pb-4 dark:bg-gray-900/30">
      <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-gray-700">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3 font-semibold">Agency</th>
            <th className="py-2 pr-3 font-semibold">Contact</th>
            <th className="py-2 pr-3 font-semibold">Location</th>
            <th className="py-2 pr-3 font-semibold">Details</th>
            <th className="py-2 font-semibold">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-gray-700">
          {area.vendors.map((v) => (
            <tr key={v.vendorId} className="align-top">
              <td className="py-3 pr-3">
                <div className="font-medium text-slate-900 dark:text-white">{v.agencyName || '—'}</div>
                <div className="font-mono text-[11px] text-slate-500">{v.generatedId || v.vendorId}</div>
              </td>
              <td className="py-3 pr-3 text-slate-700 dark:text-gray-300">
                <div>{v.contactNumber || v.phoneNumber || '—'}</div>
                <div className="text-xs text-slate-500">{v.email || '—'}</div>
              </td>
              <td className="py-3 pr-3 text-slate-700 dark:text-gray-300">
                {[v.city, v.state].filter(Boolean).join(', ') || '—'}
              </td>
              <td className="py-3 pr-3 text-xs text-slate-600 dark:text-gray-400">
                <ul className="space-y-1">
                  {v.serviceAreas.map((sa) => (
                    <li key={sa.serviceAreaId}>
                      {[
                        sa.pinCode ? `Pin ${sa.pinCode}` : null,
                        sa.areaName,
                        sa.radiusKm != null ? `${sa.radiusKm} km radius` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </li>
                  ))}
                </ul>
              </td>
              <td className="py-3">
                <div className="flex flex-col gap-1">
                  <Link to={`/vendors/ambulance/${v.vendorId}`} className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400">
                    Open vendor
                  </Link>
                  <Link to={`/vendors/ambulance/${v.vendorId}/service-areas`} className="text-xs font-semibold text-teal-700 hover:underline dark:text-teal-400">
                    Edit areas
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
      {message}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="flex items-center justify-center py-16 text-slate-400">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  );
}

function extractError(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'response' in err) {
    return (err as { response?: { data?: { message?: string } } }).response?.data?.message;
  }
  return undefined;
}

export default AmbulanceServiceAreaComparePage;
